import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import { processError } from '../../services/ServiceUtils';
import { getEmptyMessage, getErrorMessage, getSuccessMessage } from '../../utils/MessageUtils';
import { getCurrentEnvironment, subscribeToCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { confirmDialog } from '../../utils/ConfirmDialog';
import {
    getFuturesKeysStatus, getFuturesAccount, placeFuturesOrder
} from '../../services/FuturesService';
import { getOrderPairs } from '../../services/OrdersService';

const DEFAULT_ORDER = {
    symbol: 'BTCUSDT', side: 'BUY', type: 'MARKET',
    quantity: '', limitPrice: '', leverage: '', marginType: '', reduceOnly: false
};

function Futures() {
    const [environment, setEnvironment] = useState(getCurrentEnvironment());
    const [keysStatus, setKeysStatus] = useState({ testConfigured: false, productionConfigured: false });
    const [account, setAccount] = useState({ positions: [], balance: null });
    const [closedTrades, setClosedTrades] = useState(null); // pares de futuros fechados (realizado)
    const [selectedTrade, setSelectedTrade] = useState(null); // par clicado p/ exibir no modal de detalhe
    const [order, setOrder] = useState(DEFAULT_ORDER);
    const [notification, setNotification] = useState(getEmptyMessage());
    const [busy, setBusy] = useState(false);
    const closeModalRef = useRef(null);

    const configured = environment === 'test' ? keysStatus.testConfigured : keysStatus.productionConfigured;

    useEffect(() => subscribeToCurrentEnvironment(env => setEnvironment(env)), []);

    useEffect(() => {
        getFuturesKeysStatus().then(setKeysStatus).catch(err => setNotification(getErrorMessage(processError(err))));
    }, []);

    useEffect(() => {
        if (!configured) { setAccount({ positions: [], balance: null }); return; }
        loadAccount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [environment, configured]);

    function loadAccount() {
        getFuturesAccount(environment).then(setAccount).catch(err => setNotification(getErrorMessage(processError(err))));
        // Operações fechadas de FUTUROS (resultado realizado do short/long) — pares já cientes
        // de direção no backend (short abre vendendo/fecha comprando). Lucro = sell.net − buy.net.
        getOrderPairs(null, 1, environment, 50, null, 'futures')
            .then(res => setClosedTrades((res.rows || []).filter(r => r.type === 'pair')))
            .catch(() => setClosedTrades([]));
    }

    function onOrderChange(event) {
        const { id, value, type, checked } = event.target;
        setOrder(prev => ({ ...prev, [id]: type === 'checkbox' ? checked : value }));
    }

    function onPlaceOrder(event) {
        event.preventDefault();
        setBusy(true);
        const payload = {
            symbol: order.symbol.toUpperCase(), side: order.side, type: order.type,
            quantity: order.quantity, environment, reduceOnly: order.reduceOnly
        };
        if (order.type === 'LIMIT') payload.limitPrice = order.limitPrice;
        if (order.leverage) payload.leverage = order.leverage;
        if (order.marginType) payload.marginType = order.marginType;

        placeFuturesOrder(payload)
            .then(() => {
                setNotification(getSuccessMessage(`Ordem ${order.side} ${order.symbol} enviada.`));
                setOrder(DEFAULT_ORDER);
                if (closeModalRef.current) closeModalRef.current.click();
                setTimeout(loadAccount, 1200);
            })
            .catch(err => setNotification(getErrorMessage(processError(err))))
            .finally(() => setBusy(false));
    }

    // Fecha a posição em 1 clique (ordem MARKET oposta, reduceOnly).
    async function onClosePosition(pos) {
        const ok = await confirmDialog({
            title: 'Fechar posição',
            message: `Fechar a posição ${pos.side} de ${pos.symbol}?`,
            confirmLabel: 'Fechar posição',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        setBusy(true);
        placeFuturesOrder({
            symbol: pos.symbol, side: pos.side === 'LONG' ? 'SELL' : 'BUY',
            type: 'MARKET', quantity: Math.abs(pos.amount), reduceOnly: true, environment
        })
            .then(() => { setNotification(getSuccessMessage(`Posição ${pos.symbol} fechada.`)); setTimeout(loadAccount, 1200); })
            .catch(err => setNotification(getErrorMessage(processError(err))))
            .finally(() => setBusy(false));
    }

    function fmt(n, d = 2) {
        const v = parseFloat(n);
        return isNaN(v) ? '-' : v.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });
    }

    return (
        <>
            <Menu />
            <main className='content'>
                <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4'>
                    <div className='d-block mb-4 mb-md-0'>
                        <h2 className='h4'>Futuros (Derivativos USDT-M)</h2>
                        <p className='mb-0 text-muted' style={{ fontSize: '0.85em' }}>
                            Operações alavancadas — risco de liquidação. Ambiente atual: <strong>{environment === 'test' ? 'Testnet' : 'Produção'}</strong>.
                        </p>
                    </div>
                    {configured && (
                        <div>
                            <button className='btn btn-outline-primary btn-sm me-2' onClick={loadAccount} disabled={busy}>Atualizar</button>
                            <button className='btn btn-primary btn-sm' data-bs-toggle='modal' data-bs-target='#modalFuturesOrder'>Nova Ordem</button>
                        </div>
                    )}
                </div>

                {!configured && (
                    <div className='alert alert-warning'>
                        As chaves de futuros do ambiente <strong>{environment === 'test' ? 'Testnet' : 'Produção'}</strong> ainda não estão configuradas.{' '}
                        <Link to='/settings'>Configurar em Configurações → Corretora</Link>.
                    </div>
                )}

                {configured && (
                    <div className='card card-body shadow border-0 mb-4'>
                        <h5 className='h6'>Conta de Margem (USDT-M)</h5>
                        {account.balance
                            ? <p className='mb-3'>Saldo: <strong>{fmt(account.balance.balance)} USDT</strong> · Disponível: <strong>{fmt(account.balance.available)} USDT</strong></p>
                            : <p className='text-muted'>Sem saldo de margem.</p>}
                        <h6 className='h6'>Posições abertas</h6>
                        {account.positions.length === 0
                            ? <p className='text-muted mb-0'>Nenhuma posição aberta.</p>
                            : (
                                <div className='table-responsive'>
                                    <table className='table table-sm align-middle'>
                                        <thead>
                                            <tr>
                                                <th>Símbolo</th><th>Lado</th><th>Qtde</th><th>Entrada</th>
                                                <th>Marca</th><th>PnL ñ-real.</th><th>Liquidação</th><th>Alav.</th><th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {account.positions.map(p => (
                                                <tr key={p.symbol}>
                                                    <td>{p.symbol}</td>
                                                    <td><span className={`badge ${p.side === 'LONG' ? 'bg-success' : 'bg-danger'}`}>{p.side}</span></td>
                                                    <td>{p.amount}</td>
                                                    <td>{fmt(p.entryPrice)}</td>
                                                    <td>{fmt(p.markPrice)}</td>
                                                    <td className={p.unRealizedProfit >= 0 ? 'text-success' : 'text-danger'}>{fmt(p.unRealizedProfit)}</td>
                                                    <td>{fmt(p.liquidationPrice)}</td>
                                                    <td>{p.leverage}x</td>
                                                    <td><button className='btn btn-outline-danger btn-sm' onClick={() => onClosePosition(p)} disabled={busy}>Fechar</button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                    </div>
                )}

                {configured && closedTrades && closedTrades.length > 0 && (() => {
                    const rows = closedTrades.map(t => {
                        const lucro = (parseFloat(t.sell && t.sell.net) || 0) - (parseFloat(t.buy && t.buy.net) || 0);
                        // Entrada = perna mais antiga; saída = mais recente (short abre vendendo).
                        const legs = [t.buy, t.sell].filter(Boolean).sort((a, b) => Number(a.transactTime) - Number(b.transactTime));
                        const entry = legs[0], exit = legs[1] || legs[0];
                        const dir = t.sell && t.buy && Number(t.sell.transactTime) < Number(t.buy.transactTime) ? 'SHORT' : 'LONG';
                        return { symbol: (entry && entry.symbol) || (exit && exit.symbol), dir, lucro,
                            entryPrice: entry && entry.avgPrice, exitPrice: exit && exit.avgPrice,
                            qty: (t.sell && t.sell.quantity) || (t.buy && t.buy.quantity), when: exit && exit.transactTime,
                            entry, exit };
                    });
                    const total = rows.reduce((s, r) => s + r.lucro, 0);
                    return (
                        <div className='card card-body shadow border-0 mb-4'>
                            <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap'>
                                <h5 className='h6 mb-0'>Operações Fechadas (Futuros)</h5>
                                <h5 className={`mb-0 ${total > 0 ? 'text-success' : total < 0 ? 'text-danger' : 'text-muted'}`}>
                                    {total >= 0 ? '+' : ''}${total.toFixed(2)}
                                </h5>
                            </div>
                            <small className='text-muted mb-3 d-block'>Resultado realizado dos pares abertura→fechamento de futuros (ambiente atual).</small>
                            <div className='table-responsive'>
                                <table className='table table-sm table-hover align-middle mb-0'>
                                    <thead>
                                        <tr>
                                            <th>Contrato</th><th>Direção</th><th className='text-end'>Entrada</th>
                                            <th className='text-end'>Saída</th><th className='text-end'>Qtde</th><th className='text-end'>Resultado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr key={i} style={{ cursor: 'pointer' }} title='Clique para ver os detalhes da operação'
                                                onClick={() => { setSelectedTrade(r); if (window.bootstrap) window.bootstrap.Modal.getOrCreateInstance(document.getElementById('modalFuturesTrade')).show(); }}>
                                                <td><strong>{r.symbol}</strong></td>
                                                <td><span className={`badge ${r.dir === 'SHORT' ? 'bg-danger' : 'bg-success'}`}>{r.dir}</span></td>
                                                <td className='text-end' style={{ fontSize: 13 }}>{fmt(r.entryPrice)}</td>
                                                <td className='text-end' style={{ fontSize: 13 }}>{fmt(r.exitPrice)}</td>
                                                <td className='text-end text-muted' style={{ fontSize: 13 }}>{r.qty}</td>
                                                <td className={`text-end fw-bold ${r.lucro > 0 ? 'text-success' : r.lucro < 0 ? 'text-danger' : 'text-muted'}`} style={{ fontSize: 13 }}>
                                                    {r.lucro >= 0 ? '+' : ''}${r.lucro.toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })()}

                <Footer />
            </main>

            {/* Modal: Nova Ordem de Futuros */}
            <div className='modal fade' id='modalFuturesOrder' tabIndex='-1' aria-hidden='true'>
                <div className='modal-dialog modal-lg'>
                    <div className='modal-content'>
                        <div className='modal-header'>
                            <h5 className='modal-title'>Nova Ordem de Futuros <span className='text-muted' style={{ fontSize: '0.7em' }}>({environment === 'test' ? 'Testnet' : 'Produção'})</span></h5>
                            <button type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Fechar' ref={closeModalRef}></button>
                        </div>
                        <form onSubmit={onPlaceOrder}>
                            <div className='modal-body'>
                                <div className='row'>
                                    <div className='col-md-3 mb-3'>
                                        <label className='form-label' htmlFor='symbol'>Contrato</label>
                                        <input type='text' id='symbol' className='form-control' value={order.symbol} onChange={onOrderChange} placeholder='BTCUSDT' />
                                    </div>
                                    <div className='col-md-3 mb-3'>
                                        <label className='form-label' htmlFor='side'>Lado</label>
                                        <select id='side' className='form-select' value={order.side} onChange={onOrderChange}>
                                            <option value='BUY'>BUY (Long)</option>
                                            <option value='SELL'>SELL (Short)</option>
                                        </select>
                                    </div>
                                    <div className='col-md-3 mb-3'>
                                        <label className='form-label' htmlFor='type'>Tipo</label>
                                        <select id='type' className='form-select' value={order.type} onChange={onOrderChange}>
                                            <option value='MARKET'>MARKET</option>
                                            <option value='LIMIT'>LIMIT</option>
                                        </select>
                                    </div>
                                    <div className='col-md-3 mb-3'>
                                        <label className='form-label' htmlFor='quantity'>Quantidade</label>
                                        <input type='number' step='any' id='quantity' className='form-control' value={order.quantity} onChange={onOrderChange} placeholder='0.002' />
                                    </div>
                                </div>
                                <div className='row'>
                                    {order.type === 'LIMIT' && (
                                        <div className='col-md-3 mb-3'>
                                            <label className='form-label' htmlFor='limitPrice'>Preço limite</label>
                                            <input type='number' step='any' id='limitPrice' className='form-control' value={order.limitPrice} onChange={onOrderChange} placeholder='0' />
                                        </div>
                                    )}
                                    <div className='col-md-3 mb-3'>
                                        <label className='form-label' htmlFor='leverage'>Alavancagem</label>
                                        <input type='number' id='leverage' className='form-control' value={order.leverage} onChange={onOrderChange} placeholder='ex: 3' />
                                    </div>
                                    <div className='col-md-3 mb-3'>
                                        <label className='form-label' htmlFor='marginType'>Margem</label>
                                        <select id='marginType' className='form-select' value={order.marginType} onChange={onOrderChange}>
                                            <option value=''>(manter)</option>
                                            <option value='ISOLATED'>ISOLATED</option>
                                            <option value='CROSSED'>CROSSED</option>
                                        </select>
                                    </div>
                                    <div className='col-md-3 mb-3 d-flex align-items-end'>
                                        <div className='form-check'>
                                            <input className='form-check-input' type='checkbox' id='reduceOnly' checked={order.reduceOnly} onChange={onOrderChange} />
                                            <label className='form-check-label' htmlFor='reduceOnly'>Reduce Only (só reduz/fecha)</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='modal-footer'>
                                <button type='button' className='btn btn-light' data-bs-dismiss='modal'>Cancelar</button>
                                <button type='submit' className={`btn ${order.side === 'BUY' ? 'btn-success' : 'btn-danger'}`} disabled={busy}>
                                    Enviar ordem {order.side}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Modal: detalhe da operação fechada (par entrada→saída) */}
            <div className='modal fade' id='modalFuturesTrade' tabIndex='-1' aria-hidden='true'>
                <div className='modal-dialog modal-lg'>
                    <div className='modal-content'>
                        <div className='modal-header'>
                            <h5 className='modal-title'>
                                Detalhe da Operação {selectedTrade && <span className='text-muted' style={{ fontSize: '0.7em' }}>({selectedTrade.symbol})</span>}
                            </h5>
                            <button type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Fechar'></button>
                        </div>
                        <div className='modal-body'>
                            {selectedTrade && (() => {
                                const leg = (l, label, badge) => (
                                    <div className='card card-body mb-2'>
                                        <div className='d-flex justify-content-between align-items-center mb-2'>
                                            <strong>{label}</strong>
                                            {l && l.side && <span className={`badge ${l.side === 'SELL' ? 'bg-danger' : 'bg-success'}`}>{l.side}</span>}
                                        </div>
                                        {l ? (
                                            <div className='row g-2' style={{ fontSize: 13 }}>
                                                <div className='col-6 col-md-3'><span className='text-muted d-block'>Preço médio</span>{fmt(l.avgPrice)}</div>
                                                <div className='col-6 col-md-3'><span className='text-muted d-block'>Quantidade</span>{l.quantity}</div>
                                                <div className='col-6 col-md-3'><span className='text-muted d-block'>Valor (net)</span>{l.net != null ? `$${fmt(l.net)}` : '—'}</div>
                                                <div className='col-6 col-md-3'><span className='text-muted d-block'>Tipo</span>{l.type || '—'}</div>
                                                <div className='col-6 col-md-6'><span className='text-muted d-block'>ID da ordem</span><span style={{ fontFamily: 'monospace' }}>{l.orderId || '—'}</span></div>
                                                <div className='col-6 col-md-6'><span className='text-muted d-block'>Data/hora</span>{l.transactTime ? new Date(Number(l.transactTime)).toLocaleString() : '—'}</div>
                                            </div>
                                        ) : <span className='text-muted'>—</span>}
                                    </div>
                                );
                                return (
                                    <>
                                        <div className='d-flex justify-content-between align-items-center mb-3 flex-wrap'>
                                            <span className={`badge ${selectedTrade.dir === 'SHORT' ? 'bg-danger' : 'bg-success'}`}>{selectedTrade.dir}</span>
                                            <h5 className={`mb-0 ${selectedTrade.lucro > 0 ? 'text-success' : selectedTrade.lucro < 0 ? 'text-danger' : 'text-muted'}`}>
                                                Resultado: {selectedTrade.lucro >= 0 ? '+' : ''}${selectedTrade.lucro.toFixed(2)}
                                            </h5>
                                        </div>
                                        {leg(selectedTrade.entry, 'Entrada (abertura)')}
                                        {leg(selectedTrade.exit, 'Saída (fechamento)')}
                                    </>
                                );
                            })()}
                        </div>
                        <div className='modal-footer'>
                            <button type='button' className='btn btn-secondary' data-bs-dismiss='modal'>Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default Futures;
